import React, {
	useCallback,
	useState,
	useRef,
	useEffect,
	useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";

const TOOLTIP_OFFSET = 8;
const VIEWPORT_MARGIN = 8;
const TOOLTIP_EXIT_MS = 80;

export const TooltipBadge = ({ children, className = "" }) => (
	<span
		className={`inline-flex shrink-0 items-center rounded-full border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-1.5 py-px text-[10px] font-medium uppercase text-[var(--color-text-secondary)] ${className}`}
	>
		{children}
	</span>
);

export const TooltipBadgeContent = ({ children, badge, className = "" }) => (
	<div className={`inline-flex max-w-full items-center gap-2 ${className}`}>
		<p className="m-0 min-w-0 whitespace-nowrap text-[var(--color-text-primary)]">
			{children}
		</p>
		{badge && <TooltipBadge>{badge}</TooltipBadge>}
	</div>
);

export const Tooltip = ({
	content,
	listItems,
	children,
	className = "",
	placement = "top",
}) => {
	const [show, setShow] = useState(false);
	// `mounted` keeps the tooltip in the DOM during its fade-out; `closing`
	// swaps the fade-in animation for the fade-out one.
	const [mounted, setMounted] = useState(false);
	const [closing, setClosing] = useState(false);
	const [position, setPosition] = useState({
		top: 0,
		left: 0,
		placement: "top",
	});
	const triggerRef = useRef(null);
	const tooltipRef = useRef(null);

	useEffect(() => {
		if (show) {
			setMounted(true);
			setClosing(false);
			return undefined;
		}
		if (!mounted) return undefined;

		setClosing(true);
		const timer = setTimeout(() => {
			setMounted(false);
			setClosing(false);
		}, TOOLTIP_EXIT_MS);
		return () => clearTimeout(timer);
	}, [show, mounted]);

	const updatePosition = useCallback(() => {
		if (!triggerRef.current) return;

		const triggerRect = triggerRef.current.getBoundingClientRect();
		const tooltipRect = tooltipRef.current?.getBoundingClientRect();
		const tooltipWidth = tooltipRect?.width || 0;
		const tooltipHeight = tooltipRect?.height || 0;
		const minLeft = VIEWPORT_MARGIN + tooltipWidth / 2;
		const maxLeft = window.innerWidth - VIEWPORT_MARGIN - tooltipWidth / 2;
		const unclampedLeft = triggerRect.left + triggerRect.width / 2;
		const left =
			tooltipWidth > 0
				? Math.min(Math.max(unclampedLeft, minLeft), Math.max(minLeft, maxLeft))
				: Math.min(
						Math.max(unclampedLeft, VIEWPORT_MARGIN),
						window.innerWidth - VIEWPORT_MARGIN,
					);
		const preferredPlacement = placement === "bottom" ? "bottom" : "top";
		const canShowAbove =
			tooltipHeight === 0 ||
			triggerRect.top - tooltipHeight - TOOLTIP_OFFSET >= VIEWPORT_MARGIN;
		const canShowBelow =
			tooltipHeight === 0 ||
			triggerRect.bottom + tooltipHeight + TOOLTIP_OFFSET <=
				window.innerHeight - VIEWPORT_MARGIN;
		const resolvedPlacement =
			preferredPlacement === "bottom"
				? canShowBelow || !canShowAbove
					? "bottom"
					: "top"
				: canShowAbove || !canShowBelow
					? "top"
					: "bottom";

		setPosition({
			left,
			placement: resolvedPlacement,
			top:
				resolvedPlacement === "bottom"
					? triggerRect.bottom + TOOLTIP_OFFSET
					: triggerRect.top - TOOLTIP_OFFSET,
		});
	}, [placement]);

	useLayoutEffect(() => {
		if (!show) return;
		updatePosition();
	}, [show, updatePosition]);

	useLayoutEffect(() => {
		if (!mounted) return;
		updatePosition();
	}, [mounted, updatePosition]);

	useEffect(() => {
		if (!show) return undefined;

		const handlePositionChange = () => updatePosition();
		window.addEventListener("resize", handlePositionChange);
		document.addEventListener("scroll", handlePositionChange, true);

		return () => {
			window.removeEventListener("resize", handlePositionChange);
			document.removeEventListener("scroll", handlePositionChange, true);
		};
	}, [show, updatePosition]);

	if (!content) {
		return children;
	}

	const tooltip =
		mounted &&
		createPortal(
			// Outer wrapper owns the positioning transform; the inner element owns
			// the fade/slide/scale animation so the two transforms never conflict.
			<div
				ref={tooltipRef}
				className={`pointer-events-none fixed z-[9999] ${
					position.placement === "bottom"
						? "-translate-x-1/2"
						: "-translate-x-1/2 -translate-y-full"
				}`}
				style={{
					top: `${position.top}px`,
					left: `${position.left}px`,
				}}
			>
				<div
					role="tooltip"
					className={`w-max max-w-[min(22rem,calc(100vw-2rem))] rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-2.5 py-1.5 text-xs text-[var(--color-text-primary)] shadow-lg ${
						closing ? "tooltip-pop-out" : "tooltip-pop-in"
					}`}
				>
					{content}
					{listItems && (
						<ul className="mt-1 list-disc space-y-1 pl-4 text-[var(--color-text-secondary)]">
							{listItems.map((item) => (
								<li key={item}>{item}</li>
							))}
						</ul>
					)}
				</div>
			</div>,
			document.body,
		);

	return (
		<div
			ref={triggerRef}
			className={`relative inline-flex ${className}`}
			onMouseEnter={() => setShow(true)}
			onMouseLeave={() => setShow(false)}
			onFocus={() => setShow(true)}
			onBlur={() => setShow(false)}
		>
			{tooltip}
			{children}
		</div>
	);
};
